import type { Feature } from "@/services/types";

const UPGRADE_REQUIRED_EVENT = "entitlement:upgrade-required";

/** Backend error codes that mean "the user's plan doesn't allow this" (mirror ErrorCode.java). */
export const PLAN_GATE_ERROR_CODES = new Set(["FEATURE_NOT_IN_PLAN", "PLAN_LIMIT_REACHED"]);

export interface UpgradeRequiredDetail {
  /** The backend error code that triggered it. */
  code: string;
  /** The feature involved, if the caller could infer it (usually null from a raw 403). */
  feature?: Feature | null;
}

/**
 * Global signal that a backend call was rejected because the user's plan doesn't include the
 * action. The API client emits this for {@link PLAN_GATE_ERROR_CODES}; the app-root
 * UpgradePromptHost listens and opens the shared upgrade dialog. This is the safety net for
 * any gated action that wasn't pre-empted by a client-side check.
 */
export function emitUpgradeRequired(detail: UpgradeRequiredDetail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<UpgradeRequiredDetail>(UPGRADE_REQUIRED_EVENT, { detail }));
}

export function addUpgradeRequiredListener(handler: (detail: UpgradeRequiredDetail) => void) {
  const wrapped = (e: Event) => handler((e as CustomEvent<UpgradeRequiredDetail>).detail);
  window.addEventListener(UPGRADE_REQUIRED_EVENT, wrapped);
  return () => window.removeEventListener(UPGRADE_REQUIRED_EVENT, wrapped);
}
